import EventEmitter from './EventEmiter'

/**
 * The emitter of the `EventManager` mounted now, or null between containers.
 *
 * Module state, because the question it answers ("which container is on
 * screen?") belongs to no single hook instance. `templates/Default.tsx` and
 * `templates/CatalogFixed.tsx` each mount one `EventManager` per container,
 * and `<Routes>` mounts one container at a time.
 */
let mountedEmitter: EventEmitter | null = null

/** Told the mounted emitter each time it changes. */
type MountedEmitterListener = (emitter: EventEmitter | null) => void

const mountedEmitterListeners = new Set<MountedEmitterListener>()

const notifyMountedEmitterListeners = () => {
  // A copy: a listener may unsubscribe itself as it runs.
  const listeners = Array.from(mountedEmitterListeners)
  listeners.forEach((listener) => {
    listener(mountedEmitter)
  })
}

/** The emitter of the `EventManager` mounted now, or null. */
export const getMountedEmitter = () => mountedEmitter

/**
 * Calls `listener` with the mounted emitter each time a container registers
 * or unregisters, until the returned function is called (COM-1296). For
 * `useOrderConfirmation`, which holds an after-order entry until the screen
 * after the cart is up.
 *
 * Called from inside `EventManager`'s mount effect, before the effects
 * declared after it in the same commit have attached that container's
 * listeners. A listener that emits must defer it (a promise continuation is
 * enough: React flushes a commit's effects in one synchronous pass).
 */
export const subscribeMountedEmitter = (listener: MountedEmitterListener) => {
  mountedEmitterListeners.add(listener)
  return () => {
    mountedEmitterListeners.delete(listener)
  }
}

/**
 * Called by `EventManager` on mount. React runs a commit's unmount cleanups
 * before its mount effects, so on a navigation the outgoing container clears
 * first and the incoming one is what stays registered.
 */
export const registerMountedEmitter = (emitter: EventEmitter) => {
  mountedEmitter = emitter
  notifyMountedEmitterListeners()
}

/**
 * Called by `EventManager` on unmount. Clears only its own registration, so
 * a container that unmounts after another has registered cannot clear that
 * one.
 */
export const unregisterMountedEmitter = (emitter: EventEmitter) => {
  if (mountedEmitter !== emitter) {
    return
  }
  mountedEmitter = null
  notifyMountedEmitterListeners()
}

/**
 * An emitter that forwards each `emit` to whichever `EventManager` is
 * mounted when it is called (COM-1296).
 *
 * For the after-order steps. They are started by `useOrderConfirmation` on the
 * instance that owns the attempt, which is the cart's, and the cart is gone
 * by the time their handlers run, on either settle path:
 *
 * - **Polled settle** (the order is still pending when `createAndConfirmOrder`
 *   returns, as with a create-order add-on): `CREATE_ORDER` drops on that
 *   return, `usePlaceOrderFlow` pushes `pushAfter`, the cart unmounts, and
 *   the settle comes later from a poll tick. The cart's emitter has been
 *   `offAll()`ed before the steps even start.
 * - **Synchronous settle** (no create-order add-on: the create is terminal,
 *   or the awaited confirm is): the settle runs before
 *   `createAndConfirmOrder` returns, with the cart still mounted, and starts
 *   the first step there. The push lands only when `CREATE_ORDER` drops,
 *   after that return (the push gate ignores the list's `WORKFLOW_STEP`
 *   while `ordersSlice.afterOrderRunning` is set), so the cart
 *   unmounts while the step is in flight and is gone when its handlers run.
 *   A step that answers sooner has its handlers held until the next
 *   container registers (`beforeHandlers` in `useOrderConfirmation`).
 *
 * So a step's `onOutcome` emitted on the cart's own emitter would reach
 * nothing. Forwarding at emit time sends it to the container on screen,
 * normally the confirmation screen. With nothing mounted, an emit reaches
 * nothing, as an emit on an `offAll()`ed emitter does.
 *
 * `on` and `offAll` are inherited and never used: listeners belong to the
 * real emitters, which `EventManager` registers itself.
 */
export class LiveEventEmitter extends EventEmitter {
  emit(event: string, args: any) {
    if (!mountedEmitter) {
      return undefined
    }
    return mountedEmitter.emit(event, args)
  }
}

/** The one instance; it holds no state of its own. */
export const liveEventEmitter = new LiveEventEmitter()

export default liveEventEmitter
